import React from 'react'
import { useMe } from '@/hooks/useMe'
import { useMyPermissions } from '@/hooks/useMyPermissions'
import { AllowedAction } from '@/data/allowedActions'

export interface PermissionRequirement {
  module: string
  action: AllowedAction
}

interface IfAnyPermissionProps {
  /**
   * Render children when the current user has ANY of these permissions.
   * sys_admin always renders (escape hatch).
   */
  permissions: PermissionRequirement[]
  /** Render this instead when none match (default: renders nothing). */
  fallback?: React.ReactNode
  /** If true, sys_admin is NOT auto-passed and must match a listed module. Default false. */
  requireExplicitForAdmin?: boolean
  children: React.ReactNode
}

/**
 * Gate used to reveal actions (buttons, menu items) when the user has the matching
 * scope-prefixed permission. Use this instead of <AdminOnly> when provider / clinic /
 * clinic_group / ad_supplier / ad_supplier_group users should also get access as long
 * as they hold the right permission.
 *
 * Example:
 *   <IfAnyPermission permissions={[
 *     { module: 'system_clinic', action: AllowedAction.D },
 *     { module: 'provider_clinic_management', action: AllowedAction.D },
 *     { module: 'clinic_group_clinic_management', action: AllowedAction.D },
 *     { module: 'clinic_clinic_management', action: AllowedAction.D },
 *   ]}>
 *     <DeleteButton />
 *   </IfAnyPermission>
 */
export default function IfAnyPermission({
  permissions,
  fallback = null,
  requireExplicitForAdmin = false,
  children,
}: IfAnyPermissionProps) {
  const { user, isLoading: isMeLoading } = useMe()
  const { hasPermission, isLoading: isPermLoading } = useMyPermissions()

  if (isMeLoading || isPermLoading) return null

  if (!requireExplicitForAdmin && user?.role_type === 'sys_admin') {
    return <>{children}</>
  }

  const allowed = permissions.some((p) => hasPermission(p.module, p.action))
  return <>{allowed ? children : fallback}</>
}
